import { AuthUser, UserRole } from "react-ai-platform-auth";

import { UserMembership, UserDetails, UserSummary, UserStatus } from "../types";
import { LIONBRIDGE_ORG_ID } from "../constants";

type _User = UserDetails | UserSummary;

function getDisplayName({ firstName, middleName, lastName }: _User): string {
  const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");
  const displayName = fullName || "[Anonymous User]";
  return displayName;
}

function getInitials({
  email,
  firstName,
  lastName,
}: Readonly<{ email?: string; firstName?: string; lastName?: string }>): string {
  const defaultInitials = "";

  let userInitials = email?.toUpperCase().charAt(0);
  if (firstName || lastName) {
    const firstNameInitial = firstName?.charAt(0).toUpperCase();
    const lastNameInitial = lastName?.charAt(0).toUpperCase();
    userInitials = [firstNameInitial, lastNameInitial].join("");
  }
  return userInitials || defaultInitials;
}

function getLastSignInTime<T extends Readonly<{ lastAuthTime?: Readonly<Date> }>>(user: T): string {
  const { lastAuthTime } = user;
  return !lastAuthTime
    ? "-"
    : `${lastAuthTime.toLocaleString("default", { month: "short" })} ${String(
        lastAuthTime.getDate()
      ).padStart(2, "0")}, ${lastAuthTime.getFullYear()}`;
}

function formatRoleDisplay(role: UserRole): string {
  switch (role) {
    case UserRole.ORGANIZATION_ADMIN:
      return "TI Employee";
    case UserRole.EMPLOYEE:
      return "TI Employee";
    case UserRole.ORGANIZATION_USER:
      return "Contributor";
    case UserRole.CONTRIBUTOR:
      return "Contributor";
    case UserRole.SAAS_ADMIN:
      return "System Admin";
    case UserRole.SYSTEM_ADMIN:
      return "System Admin";
    case UserRole.TESTING_MANAGER:
      return "Testing Manager";
    case UserRole.PROJECT_MANAGEMENT_OFFICER:
      return "Project Management Officer";
    case UserRole.SOURCING:
      return "Sourcing";
    case UserRole.SOURCING_MANAGER:
      return "Sourcing Manager";
    case UserRole.PRODUCTION:
      return "Production";
  }
}

function shouldUseCmpContributorSpecificFeatures(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }
  const isContributor =
    user.activeMembership.role === UserRole.ORGANIZATION_USER ||
    user.activeMembership.role === UserRole.CONTRIBUTOR;
  const isLionbridgeOrgMember = user.activeMembership.id === LIONBRIDGE_ORG_ID;
  const isEmployee = user.isEmployee;
  return isContributor && isLionbridgeOrgMember && !isEmployee;
}

function getActiveMembership(user?: _User): UserMembership | undefined {
  // TODO: update when multi-orgs per user
  return user?.memberships[0];
}

function getDisplayRole(user?: _User): string {
  const role = getActiveMembership(user)?.role;
  return role ? formatRoleDisplay(role) : "User";
}

function belongsToTiOrganization(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }
  return user.activeMembership.id === LIONBRIDGE_ORG_ID;
}

function getUserStatus(user: _User): UserStatus {
  const notValidAfter = user.memberships[0].notValidAfter;
  if (notValidAfter && new Date(notValidAfter.toString()) <= new Date()) {
    return UserStatus.DEACTIVATED;
  }
  return UserStatus.ACTIVE;
}

export default {
  belongsToTiOrganization,
  formatRoleDisplay,
  getActiveMembership,
  getDisplayName,
  getDisplayRole,
  getInitials,
  getLastSignInTime,
  shouldUseCmpContributorSpecificFeatures,
  getUserStatus,
};
